import { useState } from 'react';
import { AlertCircle, LogIn, UserMinus, UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { StepUpDialog } from '@/components/auth/StepUpDialog';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ApiError } from '@/lib/api';
import { writeImpersonationSnapshot } from '@/lib/auth/impersonationSession';
import { roleLabel } from '@/pages/team/teamLogic';
import {
  isStepUpRequired,
  useImpersonateUser,
  usePlatformStaffIds,
  useSetMemberAccess,
  useTenantMembers,
  type TenantMember,
} from './hooks/useUserImpersonation';

/** «Ткаченко Иван Иванович» → «ТИ»; без имени — первая буква почты. */
export function memberInitials(m: TenantMember): string {
  const name = memberName(m).trim();
  if (name && name !== m.email) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }
  return (m.email[0] ?? '?').toUpperCase();
}

/** ФИО как его знает бэкенд; если пусто — почта (никогда не пустая строка). */
export function memberName(m: TenantMember): string {
  const display = (m.display_name ?? '').trim();
  if (display) return display;
  const composed = [m.last_name, m.first_name]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return composed || m.email;
}

/** «Агроном · ПО "Северное"» — роль и подразделение одной строкой. */
export function memberScopeLabel(m: TenantMember): string {
  const scope = m.farm_name ?? m.cluster_name ?? null;
  return scope ? `${roleLabel(m.role)} · ${scope}` : roleLabel(m.role);
}

/** «был 2 дн. назад» / «не входил». */
export function lastSeenLabel(iso: string | null | undefined): string {
  if (!iso) return 'не входил';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return 'не входил';
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return 'был сегодня';
  if (days === 1) return 'был вчера';
  if (days < 7) return `был ${days} дн. назад`;
  if (days < 31) return `был ${Math.floor(days / 7)} нед. назад`;
  if (days < 365) return `был ${Math.floor(days / 30)} мес. назад`;
  return 'был больше года назад';
}

const PLATFORM_STAFF_HINT = 'Под сотрудником платформы войти нельзя';
const BLOCKED_HINT = 'Пользователь заблокирован';

/**
 * Состояние участника словами. `status` появился в ответе ручки вместе с
 * этим экраном; у старых ответов его нет — тогда судим по `is_active`, где
 * «заблокирован» и «в архиве» неразличимы.
 */
export function memberStatusLabel(m: TenantMember): string | null {
  const status = m.status ?? (m.is_active ? 'active' : 'blocked');
  if (status === 'active') return null;
  return status === 'archived' ? 'В архиве' : 'Доступ закрыт';
}

/** Архивного не «возвращают» одной кнопкой — это делает админ компании. */
export function canReopenAccess(m: TenantMember): boolean {
  return (m.status ?? (m.is_active ? 'active' : 'blocked')) === 'blocked';
}

interface CompanyUsersPanelProps {
  tenantId: string;
  tenantName: string;
}

/**
 * Список пользователей компании под раскрытой строкой админки (AK-470,
 * wireframe PR #569 экран 3).
 *
 * Отдельной страницы компании в коде нет — маршрута
 * `/platform-admin/companies/:slug` не существует. Поэтому вход под
 * пользователем живёт прямо в строке списка: новый экран ради одной кнопки
 * не заводим.
 *
 * Кто и с каким режимом входит — решает сервер по роли админа
 * (`superadmin`/`admin` → полный доступ, `support` → только чтение).
 * Здесь режим не выбирается и не показывается до входа.
 */
export function CompanyUsersPanel({
  tenantId,
  tenantName,
}: CompanyUsersPanelProps) {
  const membersQuery = useTenantMembers(tenantId);
  const staffQuery = usePlatformStaffIds();
  const impersonate = useImpersonateUser();
  const setAccess = useSetMemberAccess();

  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Закрытие доступа подтверждаем: кнопка стоит в плотной строке рядом с
  // «Войти под ним», а последствие — разорванные сессии живого человека.
  // Возврат доступа безобиден и подтверждения не требует.
  const [closeTarget, setCloseTarget] = useState<TenantMember | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [stepUpFor, setStepUpFor] = useState<TenantMember | null>(null);

  const members = membersQuery.data ?? [];
  const staffIds = staffQuery.data;

  const toggleAccess = async (member: TenantMember, blocked: boolean) => {
    setError(null);
    setNotice(null);
    if (blocked) setCloseError(null);
    try {
      await setAccess.mutateAsync({
        tenantId,
        memberId: member.member_id,
        blocked,
        reason: blocked ? 'Закрыт администратором платформы' : undefined,
      });
      if (blocked) setCloseTarget(null);
      setNotice(
        blocked
          ? `Доступ закрыт: ${memberName(member)}. Активные сессии разорваны.`
          : `Доступ возвращён: ${memberName(member)}.`,
      );
    } catch (err) {
      const text =
        err instanceof ApiError
          ? err.detail || 'Не удалось изменить доступ'
          : 'Не удалось изменить доступ';
      if (blocked) setCloseError(text);
      else setError(text);
    }
  };

  const enter = async (member: TenantMember, stepUpToken?: string) => {
    setError(null);
    setPendingUserId(member.user_id);
    try {
      const result = await impersonate.mutateAsync({
        tenantId,
        userId: member.user_id,
        stepUpToken,
      });
      // Имя и режим — из ответа сервера, не из строки таблицы: режим
      // считает бэкенд, и подписывать плашку догадкой клиента нельзя.
      writeImpersonationSnapshot({
        user_full_name: result.full_name,
        mode: result.mode,
        tenant_name: result.tenant_name,
        expires_at: result.expires_at,
      });
      // Жёсткий переход: cookie уже чужая, а в памяти висят сторы админки
      // (кэш TanStack, карта, сезон). Перезагрузка гарантирует чистый старт.
      window.location.assign('/');
    } catch (err) {
      setPendingUserId(null);
      if (isStepUpRequired(err)) {
        setStepUpFor(member);
        return;
      }
      setError(
        err instanceof ApiError
          ? err.detail || 'Не удалось войти под пользователем'
          : 'Не удалось войти под пользователем',
      );
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      {/* Рамку и фон даёт раскрытая строка (`CompanyExpandedRow`) — здесь
          только содержимое вкладки, иначе отступы удваивались бы. */}
      <div className="py-2">
        <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Пользователи компании
          {membersQuery.isSuccess ? ` — ${members.length}` : ''}
        </div>

        {membersQuery.isLoading && (
          <div className="space-y-2 px-2 py-1">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {membersQuery.isError && (
          <div className="flex items-center gap-2 px-2 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              Не удалось загрузить пользователей компании «{tenantName}»
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => membersQuery.refetch()}
            >
              Повторить
            </Button>
          </div>
        )}

        {membersQuery.isSuccess && members.length === 0 && (
          <div className="px-2 py-2 text-sm text-muted-foreground">
            В компании пока нет пользователей
          </div>
        )}

        {error && (
          <div className="px-2 pb-1 text-sm text-destructive" role="alert">
            {error}
          </div>
        )}

        {/* Успех — строкой на экране: `toast.success` в проекте сегодня
            только пишет в консоль, то есть был бы невидимым. */}
        {notice && (
          <div className="px-2 pb-1 text-sm text-muted-foreground" role="status">
            {notice}
          </div>
        )}

        <div className="divide-y divide-border/60">
          {members.map((m) => {
            const isPlatformStaff = staffIds?.has(m.user_id) ?? false;
            const blockedHint = isPlatformStaff
              ? PLATFORM_STAFF_HINT
              : !m.is_active
                ? BLOCKED_HINT
                : null;
            const isPending = pendingUserId === m.user_id;
            const button = (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!!blockedHint || pendingUserId !== null}
                onClick={() => void enter(m)}
              >
                <LogIn className="h-4 w-4" />
                {isPending ? 'Входим…' : 'Войти под ним'}
              </Button>
            );

            return (
              <div
                key={m.member_id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-2"
              >
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="text-[11px]">
                    {memberInitials(m)}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1 basis-[220px]">
                  <p className="truncate text-sm font-medium leading-tight text-foreground">
                    {memberName(m)}
                  </p>
                  <p className="truncate text-xs leading-tight text-muted-foreground">
                    {m.email}
                  </p>
                </div>

                <div className="min-w-0 basis-[200px] truncate text-xs text-muted-foreground">
                  {isPlatformStaff ? 'Сотрудник платформы' : memberScopeLabel(m)}
                </div>

                <div className="w-[110px] shrink-0">
                  {memberStatusLabel(m) ? (
                    <Badge
                      variant={
                        // Архив — не ошибка, красный оставляем блокировке.
                        m.status === 'archived' ? 'secondary' : 'destructive'
                      }
                    >
                      {memberStatusLabel(m)}
                    </Badge>
                  ) : (
                    <Badge variant="primary">Работает</Badge>
                  )}
                </div>

                <div className="w-[130px] shrink-0 text-xs text-muted-foreground">
                  {lastSeenLabel(m.last_login_at)}
                </div>

                {!isPlatformStaff &&
                  (m.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={
                        setAccess.isPending && closeTarget?.member_id === m.member_id
                      }
                      onClick={() => {
                        setCloseError(null);
                        setCloseTarget(m);
                      }}
                    >
                      <UserMinus className="h-4 w-4" />
                      Закрыть доступ
                    </Button>
                  ) : canReopenAccess(m) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={setAccess.isPending}
                      onClick={() => void toggleAccess(m, false)}
                    >
                      <UserPlus className="h-4 w-4" />
                      Вернуть доступ
                    </Button>
                  ) : null)}

                {blockedHint ? (
                  <Tooltip>
                    {/* disabled-кнопка не шлёт pointer-события — обёртка нужна,
                        иначе подсказка не покажется. */}
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>{button}</span>
                    </TooltipTrigger>
                    <TooltipContent>{blockedHint}</TooltipContent>
                  </Tooltip>
                ) : (
                  button
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmActionDialog
        open={closeTarget !== null}
        title="Закрыть доступ в компанию?"
        description={
          closeTarget
            ? `${memberName(closeTarget)} (${closeTarget.email}) потеряет доступ к «${tenantName}» прямо сейчас — открытые вкладки закроются. Доступ к другим компаниям сохранится, вернуть можно этой же кнопкой.`
            : ''
        }
        confirmLabel="Закрыть доступ"
        destructive
        pending={setAccess.isPending}
        error={closeError}
        onConfirm={() => {
          if (closeTarget) void toggleAccess(closeTarget, true);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setCloseTarget(null);
            setCloseError(null);
          }
        }}
      />

      {stepUpFor && (
        <StepUpDialog
          open
          onOpenChange={(open) => {
            if (!open) setStepUpFor(null);
          }}
          purpose="impersonate"
          /* Почта пользователя, под которого входим — чтобы было видно,
             в чью учётку ведёт подтверждение. Код при этом свой, админский:
             сервер сверяет его с текущим пользователем. */
          accountLabel={stepUpFor.email}
          onConfirmed={(token) => {
            const target = stepUpFor;
            setStepUpFor(null);
            void enter(target, token);
          }}
        />
      )}
    </TooltipProvider>
  );
}

export default CompanyUsersPanel;
